import {
    AccountType,
    CryptoOrderTransaction,
    OrderActualTransaction,
    OrderMarket,
    OrderMarketTime,
    OrderStatus,
    OrderTransaction,
    OrderType,
    OrderValidity,
} from '../../constants/lookups';
import {
    CryptoTradingFormValues,
    CancelledCryptoOrderInfo,
    TradingFormMeta,
    TradingFormValues,
    CryptoTradePreview,
    OrderWithExtraInfo,
} from '../../types';
import { divide, multiply, parseCryptoSymbol, subtract, sum } from '../../utils';
import { TradingApi } from '../TradingApi';
import { getUuid } from './utils';
import numbro from 'numbro';
import { injectable } from 'tsyringe';
export interface AxosCryptoTradingValues {
    instrument: string;
    type: CryptoOrderTransaction;
    amount: number;
    price: number;
    activationPrice: number;
    isLimit: boolean;
    isStop: boolean;
    useFeeDiscount: boolean;
    selfMatchStrategy: number;
    selfMatchToken: string;
    side: number;
    orderType: number;
    requestedQuoteAmount?: number;
}

@injectable()
export class AxosTradingApi extends TradingApi {

    protected override prepareOrder(values: TradingFormValues, meta: TradingFormMeta) {
        const orderBody = super.prepareOrder(values, meta);

        if (meta.mutualFundTrading) {
            orderBody.order.capitalGains = values.mutualFundCapitalGains;
            orderBody.order.dividends = values.mutualFundDividends;
        }

        return orderBody;
    }

    private async prepareAxosCryptoOrder(values: CryptoTradingFormValues): Promise<AxosCryptoTradingValues> {

        const formatOptions: numbro.Format = {
            mantissa: 4,
            roundingFunction: Math.floor
        };

        const quote = await this.quoteApi.getQuote(parseCryptoSymbol(values.coin).symbol, true);
        if (values.tradeWithStablecoin && values.orderType === OrderType.Limit) {
            return {
                instrument: values.coin,
                type: values.transaction,
                amount: numbro.unformat(numbro(divide(values.quantity ?? 0, quote?.lastPrice ?? 0)).format(formatOptions)),
                price: values.limitPrice || 0,
                activationPrice: 0,
                isLimit: values.orderType === OrderType.Limit,
                isStop: false,
                useFeeDiscount: true,
                selfMatchStrategy: 0,
                selfMatchToken: getUuid(),
                side: 0,
                orderType: 0,
            };
        }
        if (values.tradeWithStablecoin) {
            return {
                instrument: values.coin,
                type: values.transaction,
                amount: 0,
                price: values.orderType === OrderType.Limit ? values.limitPrice || 0 : 0,
                activationPrice: 0,
                isLimit: values.orderType === OrderType.Limit,
                isStop: values.orderType === OrderType.Stop,
                useFeeDiscount: true,
                selfMatchStrategy: 0,
                selfMatchToken: getUuid(),
                side: 0,
                orderType: 0,
                requestedQuoteAmount: values.quantity,
            };
        }

        return {
            instrument: values.coin,
            type: values.transaction,
            amount: values.quantity || 0,
            price: values.orderType === OrderType.Limit ? values.limitPrice || 0 : 0,
            activationPrice: 0,
            isLimit: values.orderType === OrderType.Limit,
            isStop: values.orderType === OrderType.Stop,
            useFeeDiscount: true,
            selfMatchStrategy: 0,
            selfMatchToken: getUuid(),
            side: 0,
            orderType: 0,
        };
    }

    /* eslint-disable @typescript-eslint/no-unused-vars */

    override async previewCryptoOrder(
        values: CryptoTradingFormValues,
        accountNumber: string,
    ): Promise<CryptoTradePreview> {
        return {
            expectedQty: 0,
            expectedPx: 0,
            warnings: [],
            orderFeesCharges: 0,
        };
    }

    /* eslint-enable @typescript-eslint/no-unused-vars */

    override async placeCryptoOrder(
        values: CryptoTradingFormValues,
        accountNumber: string,
    ): Promise<OrderWithExtraInfo> {
        const order = await this.prepareAxosCryptoOrder(values);
        order.instrument = parseCryptoSymbol(order.instrument, '_').symbol.toLowerCase();
        const requestId = getUuid();
        const response = await this.sdks.middleware.post(
            // @ts-ignore: URL not in SDK
            'axos/crypto/trading/orders',
            {
                requestId,
                requests: [{ accountNumber: accountNumber, ...order }],
            },
        );
        return { orderRef: requestId, ...response.data.entity[0].order };
    }

    override async getCryptoOrderDetails(
        accountNumber: string,
        orderRef: string,
    ): Promise<OrderWithExtraInfo | null> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in SDK
            'axos/crypto/trading/orders',
            undefined,
            {
                params: {
                    accountNumber,
                    requestId: orderRef,
                },
            },
        );
        if (!response.data?.entity) {
            return null;
        }
        const order = response.data?.entity?.[0];
        return {
            validity: OrderValidity.GoodForDay,
            marketTime: OrderMarketTime.CoreMarket,
            execTime: order.createdAt,
            symbol: order.instrument,
            ourRef: order.orderId,
            transType: OrderActualTransaction.Buy,
            market: OrderMarket.Us,
            theirRef: '',
            clientRef: '',
            contraOid: 0,
            aon: 0,
            margin: 0,
            requestState: 0,
            accountType: AccountType.Cash,
            accountId: 0,
            optionFlag: 0,
            legRef: 0,
            noLegs: 0,
            expectedValue: 0,
            quantity: order?.requestedQuoteAmount === 0 ? order?.amount : order?.requestedQuoteAmount,
            execQty: 0,
            remainingQty: 0,
            unallocatedQty: 0,
            limitPx: order.limitPrice,
            stopPx: order.stopPrice,
            execPx: order.executionPrice,
            expectedPx: order.expectedPx,
            replaceable: false,
            createDate: '',
            executedDate: order.createdAt,
            option: false,
            mutualFund: false,
            translatedStatus: OrderStatus.AcceptedAtTheExchange,
            multilegOrderType: '',
            marketSpreadPrice: 0,
            executedSpreadPrice: 0,
            expectedSpreadPx: 0,
            cancelable: false,
            algoOrder: false,
            allocation: false,
            actualTransaction: OrderActualTransaction.Buy,
            modelId: 0,
            orderRef: order.orderId,
            companyName: '',
            model: '',
            fullName: '',
            accountNumber: '',
            tradeType: '',
            securityType: '',
            value: 0,
            costValue: 0,
            transaction: OrderTransaction.Buy,
            commission: order.commission,
            commissionCurrency: order.commissionCurrency,
            createdBy: order.createdBy,
            isLimit: order.isLimit,
            isPending: order.isPending,
            orderType: order?.orderType,
            remaining: order.remaining,
            status: order.status,
            tradedAmount: order.tradedAmount,
            unitsFilled: order?.unitsFilled ?? subtract(order?.quantity ?? 0, order?.remainingQty ?? 0),
            rejectDetails: order.rejectedDetails,
            cancelReason: order.cancelReason,
            requestId: order.requestId,
            messages: response.data.status !== 200 ? order.messages : [],
            total: sum(
                multiply(order.executionPrice, order.tradedAmount),
                // When selling, commission comes back as USD. When buying, commission value is in coin traded 
                order.type === CryptoOrderTransaction.Sell
                    ? order.commission
                    : multiply(order.executionPrice, order.commission),
            ),
        };
    }

    override async cancelCryptoOrder(accountNumber: string, orderId: string): Promise<CancelledCryptoOrderInfo> {
        const requestId = getUuid();
        const response = await this.sdks.middleware.delete(
            // @ts-ignore: URL not in SDK
            'axos/crypto/trading/orders',
            {
                requestId,
                requests: [{ accountNumber: accountNumber, orderId: orderId }],
            },
        );
        return response.data.entity[0];
    }
}
